<template>
  <div class="container">
    <div class="my-4">
      <SizingsAdvancedSearch :sizingsSearch="sizingsSearch" hide-owner />
    </div>
    <div class="flex justify-content-end my-2">
      <Button
        class="p-button-success mr-2"
        @click="
          $router.push({
            name: 'ScenarioInfo',
            params: { sizingId: 'new', scenarioId: 'new' }
          })
        "
      >
        <icon icon="plus-circle" class="mr-1" />
        Create Sizing
      </Button>
      <EquipmentLoaderModal v-slot="{ download }">
        <Button
          v-if="isOnline"
          label="Sync Offline Equipment"
          class="p-button-info"
          @click="download"
        />
      </EquipmentLoaderModal>
    </div>
    <div>
      <p class="heading-1 my-4">My Sizings</p>
    </div>

    <OfflineSizingsTable v-if="!isOnline" hide-owner class="p-datatable-sm" />

    <SizingsTable
      v-model:limit="sizingsLimit"
      v-model:offset="sizingsOffset"
      v-model:sort="sizingMultiSortMeta"
      :sizings="sizings"
      :sizings-aggregate-count="sizingsAggregateCount"
      :loading="sizingsFetching"
      :render-links="true"
      class="p-datatable-sm"
      hide-owner
    />

    <div>
      <p class="heading-1 my-4">My Scenarios</p>
    </div>

    <OfflineScenariosTable v-if="!isOnline" hide-owner class="p-datatable-sm" />

    <ScenariosTable
      v-model:limit="scenariosLimit"
      v-model:offset="scenariosOffset"
      v-model:order-by="scenariosOrderBy"
      :render-links="true"
      :scenarios="scenarios"
      :scenarios-aggregate-count="scenariosAggregateCount"
      :loading="scenariosFetching"
      class="p-datatable-sm"
      hide-owner
    />

    <div>
      <p class="heading-1 my-4">My Evaluations</p>
    </div>
    <PerfevalsTable
      v-model:limit="perfEvalsLimit"
      v-model:offset="perfEvalsOffset"
      v-model:order-by="perfEvalsOrderBy"
      :perfevals="perfevals"
      :perfevals-aggregate-count="perfevalsAggregateCount"
      :render-links="true"
      :loading="perfevalsFetching"
      class="p-datatable-sm"
      hide-owner
    />
  </div>
</template>

<script setup lang="ts">
import {
  Order_By,
  usePerformanceEvaluationsQuery,
  useScenariosQuery,
  useSizingsWithScenarioIdsQuery
} from "gql/api";

import { computed, onMounted, ref, watch } from "vue";
import type {
  PerformanceEvaluationsQueryVariables,
  Performance_Evaluations_Order_By,
  ScenariosQueryVariables,
  Scenarios_Order_By,
  SizingsWithScenarioIdsQueryVariables,
  Sizings_Order_By
} from "gql/api";

import { sizingsByOwnerIdOrHalIdentifier } from "@/models/sizings/predicates";
import { perfevalsByOwnerIdOrHalIdentifier } from "@/models/performance_evaluations/predicates";
import { scenariosByOwnerIdOrHalIdentifier } from "@/models/scenarios/predicates";

import useUserInfo from "@/composables/auth/useUserInfo";

import SizingsAdvancedSearch from "@/components/sizings/SizingsAdvancedSearch.vue";
import SizingsTable from "@/components/sizings/SizingsTable.vue";
import ScenariosTable from "@/components/sizings/ScenariosTable.vue";
import PerfevalsTable from "@/components/sizings/PerfevalsTable.vue";
import { useOnline, watchDebounced } from "@vueuse/core";
import EquipmentLoaderModal from "@/components/equipment/EquipmentLoaderModal.vue";
import OfflineSizingsTable from "@/components/sizings/OfflineSizingsTable.vue";
import OfflineScenariosTable from "@/components/sizings/OfflineScenariosTable.vue";
import { useSizingsAdvancedSearch } from "@/composables/advancedSearch/sizings";
import {
  getPrimevueSortMeta,
  getUrqlOrderByMeta,
  type SortMeta
} from "@/composables/primevue/tables";

import {
  getSizingsSearchFromQuery,
  getSortMetaFromQuery,
  getSortQueryString
} from "@/utils/sizingQueryHelpers";

import { useRouter, useRoute } from "vue-router";
const router = useRouter();
const route = useRoute();

const user = useUserInfo();
const isOnline = useOnline();

const DEFAULT_SIZING_LIMIT = 5;
const DEFAULT_SIZING_OFFSET = 0;

const sizingsLimit = ref(Number(route.query.limit) || DEFAULT_SIZING_LIMIT);
const sizingsOffset = ref(Number(route.query.offset) || DEFAULT_SIZING_OFFSET);

watch(
  () => route.query.offset,
  newV => {
    sizingsOffset.value = Number(newV) || DEFAULT_SIZING_OFFSET;
  }
);

watch(
  () => route.query.limit,
  newV => {
    sizingsLimit.value = Number(newV) || DEFAULT_SIZING_LIMIT;
  }
);

const defaultSort = [
  getPrimevueSortMeta({ journal_updated_at: Order_By.Desc })
];

const sizingMultiSortMeta = ref<SortMeta[]>(
  getSortMetaFromQuery(route.query) || [...defaultSort]
);

watch(
  () => route.query.sort,
  () => {
    sizingMultiSortMeta.value = getSortMetaFromQuery(route.query) || [
      ...defaultSort
    ];
  }
);

const sortIsDefault = computed(() =>
  sizingMultiSortMeta.value.every(
    ({ field, order }) => field === "journal_updated_at" && order === -1
  )
);

const {
  sizingsWhere,
  sizingsSearch,
  perfevalsWhere,
  scenariosWhere,
  getSearchRouteQuery
} = useSizingsAdvancedSearch({
  sizingsSearch: getSizingsSearchFromQuery(route.query),
  sizingsWhere: sizingsByOwnerIdOrHalIdentifier(
    user.id as string,
    user.hal_identifier as string
  ),
  perfevalsWhere: perfevalsByOwnerIdOrHalIdentifier(
    user.id as string,
    user.hal_identifier as string
  ),
  scenariosWhere: scenariosByOwnerIdOrHalIdentifier(
    user.id as string,
    user.hal_identifier as string
  )
});

const sizingsOrderBy = computed<Sizings_Order_By[]>(() =>
  getUrqlOrderByMeta(sizingMultiSortMeta.value)
);
const scenariosLimit = ref(5);
const scenariosOffset = ref(0);
const scenariosOrderBy = ref<Scenarios_Order_By[]>([
  {
    journal_updated_at: Order_By.Desc
  }
]);
const perfEvalsLimit = ref(5);
const perfEvalsOffset = ref(0);
const perfEvalsOrderBy = ref<Performance_Evaluations_Order_By[]>([
  {
    journal_updated_at: Order_By.Desc
  }
]);

const updateRouterQuery = () => {
  const query: Partial<{
    limit: string;
    offset: string;
    where: string;
    sort: string;
    customer?: string;
    global?: string;
    owner?: string;
    well?: string;
    dateRange?: string;
  }> = {};

  if (sizingsLimit.value !== DEFAULT_SIZING_LIMIT) {
    query.limit = sizingsLimit.value.toString();
  }

  if (sizingsOffset.value !== DEFAULT_SIZING_OFFSET) {
    query.offset = sizingsOffset.value.toString();
  }

  if (!sortIsDefault.value) {
    query.sort = getSortQueryString(sizingMultiSortMeta.value);
  }

  const searchQuery = getSearchRouteQuery();
  Object.assign(query, searchQuery || {});

  router.push({
    query
  });
};

watch(
  () => route.query,
  newQuery => {
    sizingsSearch.value = getSizingsSearchFromQuery(newQuery);
    sizingMultiSortMeta.value = getSortMetaFromQuery(newQuery) || defaultSort;
  }
);

watchDebounced(
  [
    sizingsLimit,
    sizingsOffset,
    sizingsSearch,
    sizingsWhere,
    sizingsOrderBy,
    sizingMultiSortMeta
  ],
  () => {
    updateRouterQuery();
  },
  { deep: true, debounce: 400 }
);

const sizingsVariables = computed<SizingsWithScenarioIdsQueryVariables>(() => {
  return {
    withAggregate: true,
    withImportUser: true,
    limit: sizingsLimit.value,
    offset: sizingsOffset.value,
    order_by: sizingsOrderBy.value,
    where: sizingsWhere.value
  };
});

const scenariosVariables = computed<ScenariosQueryVariables>(() => {
  return {
    withAggregate: true,
    limit: scenariosLimit.value,
    offset: scenariosOffset.value,
    order_by: scenariosOrderBy.value,
    where: scenariosWhere.value
  };
});

const perfevalsVariables = computed<PerformanceEvaluationsQueryVariables>(
  () => {
    return {
      withAggregate: true,
      limit: perfEvalsLimit.value,
      offset: perfEvalsOffset.value,
      order_by: perfEvalsOrderBy.value,
      where: perfevalsWhere.value
    };
  }
);

const { fetching: sizingsFetching, ...sizingsQuery } =
  useSizingsWithScenarioIdsQuery({
    variables: sizingsVariables
  });

const sizings = computed(() => sizingsQuery.data.value?.sizings ?? []);
const sizingsAggregateCount = computed(
  () => sizingsQuery.data.value?.sizings_aggregate?.aggregate?.count ?? 0
);

const { fetching: scenariosFetching, ...scenariosQuery } = useScenariosQuery({
  variables: scenariosVariables
});

const scenarios = computed(() => scenariosQuery.data.value?.scenarios ?? []);
const scenariosAggregateCount = computed(
  () => scenariosQuery.data.value?.scenarios_aggregate?.aggregate?.count ?? 0
);

const { fetching: perfevalsFetching, ...perfevalsQuery } =
  usePerformanceEvaluationsQuery({
    variables: perfevalsVariables
  });

// Replace r_scenario and r_author with scenario and author
const perfevals = computed(
  () =>
    perfevalsQuery.data.value?.performance_evaluations.map(
      ({ r_scenario: scenario, r_author: author, ...rest }) => ({
        scenario,
        author,
        ...rest
      })
    ) ?? []
);
const perfevalsAggregateCount = computed(
  () =>
    perfevalsQuery.data.value?.performance_evaluations_aggregate?.aggregate
      ?.count ?? 0
);

onMounted(() => {
  updateRouterQuery();
});
</script>
